/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

/** Reactions the server accepts. Kept short so the reaction bar stays one row. */
export const CHAT_ALLOWED_REACTIONS = [
  '👍',
  '❤️',
  '😂',
  '😮',
  '😢',
  '🤤',
  '😇',
  '😡',
  '🫦',
  '🔥',
  '🎉',
  '👀',
  '🙏',
  '💯',
  '🌶️',
  '🍆',
  '🍑',
  '💦',
  '😈',
] as const

export type ChatAllowedReaction = (typeof CHAT_ALLOWED_REACTIONS)[number]

const ALLOWED_REACTION_SET = new Set<string>(CHAT_ALLOWED_REACTIONS)

export const isAllowedChatReaction = (emoji: string): emoji is ChatAllowedReaction => ALLOWED_REACTION_SET.has(emoji)

/** Maximum characters of plain text in one message. */
export const CHAT_MESSAGE_MAX_LENGTH = 4000

/** Maximum media attachments on one message. */
export const CHAT_MESSAGE_MAX_MEDIA = 10

/** Window during which a sender can still edit or delete their own message. */
export const CHAT_MESSAGE_EDIT_WINDOW_MS = 24 * 60 * 60 * 1000

export const CHAT_ROOM_NAME_MAX_LENGTH = 80
export const CHAT_ROOM_DESCRIPTION_MAX_LENGTH = 500

/**
 * Default / supporter+ cap for GROUP rooms. Role-specific limits live on
 * `FeatureFlag.CHAT_GROUP_MAX_MEMBERS`. DMs are always exactly two. Channels are uncapped.
 */
export const CHAT_GROUP_MAX_MEMBERS = 50

/** Page sizes. Messages page backwards from newest; the sidebar pages forwards. */
export const CHAT_MESSAGES_PAGE_SIZE = 50

/**
 * When opening the timeline on a specific message (search jump), how many newer
 * messages to include so the hit is not glued to the bottom of the window.
 */
export const CHAT_MESSAGES_AROUND_NEWER_COUNT = 15
export const CHAT_ROOMS_PAGE_SIZE = 30
export const CHAT_MEMBERS_PAGE_SIZE = 50
export const CHAT_SEARCH_PAGE_SIZE = 25
export const CHAT_REACTIONS_PAGE_SIZE = 30

/** Shortest query the message search index will run. Prefix matching below this is too broad. */
export const CHAT_SEARCH_MIN_QUERY_LENGTH = 3

/**
 * Avatars shown in the "seen by" row. Read state is derived from
 * `ChatMember.lastReadAt`, so this is a display cap rather than a query limit.
 */
export const CHAT_SEEN_BY_DISPLAY_LIMIT = 10

/**
 * Groups larger than this do not show seen-by indicators. Channels never do;
 * DMs always do. Exactly this many members is still allowed.
 */
export const CHAT_SEEN_BY_MAX_MEMBERS = 30

/** Page size for the seen-by dialog. One page covers the member cap. */
export const CHAT_SEEN_BY_PAGE_SIZE = 30

/**
 * Whether a room should resolve and display seen-by receipts.
 * Channels are always off; groups only when memberCount is within the cap.
 */
export const chatRoomSupportsSeenBy = (room: {
  type: 'DIRECT' | 'GROUP' | 'CHANNEL'
  memberCount: number
}): boolean => {
  if (room.type === 'CHANNEL') {
    return false
  }
  if (room.type === 'DIRECT') {
    return true
  }
  return room.memberCount <= CHAT_SEEN_BY_MAX_MEMBERS
}

/**
 * Cap on `unreadBadgeCount`. Past this the UI shows "99+", so counting further is
 * wasted work.
 */
export const CHAT_UNREAD_COUNT_CAP = 99

/**
 * Rooms considered by the sidebar's unread filter. Unread state cannot be expressed as
 * a Prisma predicate, so it resolves to a bounded id list; a member past this many
 * unread rooms sees the most recent ones, which is all the tab is for.
 */
export const CHAT_UNREAD_FILTER_ROOM_CAP = 200

/** Messages one profile may send per minute, across all rooms. */
export const CHAT_SEND_RATE_LIMIT_PER_MINUTE = 30

/** Reaction toggles one profile may send per minute, across all rooms. */
export const CHAT_REACT_RATE_LIMIT_PER_MINUTE = 60

/** Read-cursor advances one profile may send per minute, across all rooms. */
export const CHAT_MARK_READ_RATE_LIMIT_PER_MINUTE = 60

/** Member add/remove/ban/role mutations one profile may send per minute. */
export const CHAT_MEMBERSHIP_RATE_LIMIT_PER_MINUTE = 30

/** Rooms one profile may create per hour. */
export const CHAT_ROOM_CREATE_RATE_LIMIT_PER_HOUR = 10

/**
 * Newest messages kept in the Apollo `chatMessages` union after a cursorless
 * refresh (poll / reconnect). Pagination still appends while the room is open;
 * leaving the room evicts the timeline.
 */
export const CHAT_MESSAGES_CACHE_MAX = 200

/** Gap after which a new day divider and a fresh message group are rendered. */
export const CHAT_MESSAGE_GROUP_WINDOW_MS = 5 * 60 * 1000

/** Debounce before `markChatRoomRead` fires while the user scrolls the timeline. */
export const CHAT_MARK_READ_DEBOUNCE_MS = 1000

/** Composer drafts are kept per room in localStorage under this key prefix. */
export const CHAT_DRAFT_STORAGE_PREFIX = 'sf.chat.draft.'

/** Coalesce Ably presence enter/leave bursts into one React state update. */
export const CHAT_PRESENCE_SYNC_DEBOUNCE_MS = 400

/**
 * How often a viewing client may persist `ChatRoom.onlineCount`. Many people arriving
 * at once must not become one GraphQL write each.
 */
export const CHAT_ONLINE_COUNT_REPORT_DEBOUNCE_MS = 10_000

/**
 * Sidebar treats `onlineCount` as zero when `onlineCountUpdatedAt` is older than this,
 * so a last-leaver who never reported cannot leave a stale badge.
 */
export const CHAT_ONLINE_COUNT_STALE_MS = 2 * 60 * 1000

/**
 * How often an open room re-reports its presence size so `onlineCountUpdatedAt` stays
 * inside the stale window while people are still there.
 */
export const CHAT_ONLINE_COUNT_HEARTBEAT_MS = 60_000
